import { useApiClient } from '@/composables/useApiClient'
import { useToast } from 'primevue'
import { useI18n } from 'vue-i18n'
import { useAuthStore } from '@/stores/useAuthStore'
import type { IFilterCampaign } from './interfaces/filter-campaign.interface'
import type { IPaginationResponse } from '../interfaces/pagination-response.interface'
import type { ICampaign } from './interfaces/campaign.interface'
import type { IOptionsCampaign } from './interfaces/options-campaign.interface'
import type { IUpdateCampaign } from './interfaces/update-campaign.interface'
import type { ChatChannel } from './enums/chat-channel.enum'
import type { TypeCampaign } from './enums/type-campaign.enum'
import type { IShiftActivity } from './interfaces/shift-activity.interface'

type CampaignOptionsFilter = {
  user_id?: string
  campaign_type?: TypeCampaign
  chat_channel?: ChatChannel
}

// Sin cache persistente a proposito: la respuesta trae contadores del dia
// (`chats_count`) que la tarjeta de campana pinta, y guardarlos en
// sessionStorage los congelaba una hora — el numero seguia en cero despues de
// que entrara el primer chat. Lo unico que se comparte es la peticion en
// vuelo, que solo evita repetir la misma consulta entre montajes simultaneos.
const campaignOptionsRequests = new Map<string, Promise<IOptionsCampaign[]>>()
const campaignDetailRequests = new Map<string, Promise<IUpdateCampaign>>()
const campaignDetailCache = new Map<string, IUpdateCampaign>()
const campaignKnowledgeDocumentListRequests = new Map<
  string,
  Promise<ICampaignKnowledgeDocumentList>
>()

const clearCampaignCache = () => {
  campaignOptionsRequests.clear()
  campaignDetailRequests.clear()
  campaignDetailCache.clear()
  campaignKnowledgeDocumentListRequests.clear()
}

export interface ICampaignKnowledgeDocument {
  id: string
  campaign_id: string
  title: string
  source_type: string
  content_hash: string
  version: number
  status: string
  active: boolean
  index_attempts: number
  chunks_count: number
  vector_collection_name?: string | null
  error_message?: string | null
  indexed_at?: string | null
  created_by_user_id?: string | null
  created_at: string
  updated_at: string
  reused_existing?: boolean
}

export interface ICampaignKnowledgeDocumentList {
  campaign_id: string
  total: number
  items: ICampaignKnowledgeDocument[]
}

export interface ICampaignKnowledgeDocumentDetail extends ICampaignKnowledgeDocument {
  content: string
}

export interface ICampaignKnowledgeChatSource {
  document_id: string
  title: string
  content: string
}

export interface ICampaignKnowledgeChatResponse {
  answer: string
  sources: ICampaignKnowledgeChatSource[]
  warning?: string | null
}

export interface ICampaignByQueueResponse {
  campaign_id: string
}

export const useCampaignService = () => {
  const privateApi = useApiClient(true)
  const toast = useToast()
  const { t } = useI18n()
  const authStore = useAuthStore()

  const showToast = (type: 'success' | 'error', messageKey: string) => {
    toast.add({
      severity: type,
      summary: t(type === 'success' ? 'general.success' : 'general.error'),
      detail: t(messageKey),
      life: 3000,
    })
  }

  const handleError = (error: unknown, messageKey: string) => {
    showToast('error', messageKey)
  }

  const getCampaigns = async (query?: IFilterCampaign): Promise<IPaginationResponse<ICampaign>> => {
    try {
      return await privateApi.get<IPaginationResponse<ICampaign>>('/campaigns', {
        params: { ...query },
      })
    } catch (error) {
      handleError(error, 'campaign.error_fetching_all_campaigns')
      throw error
    }
  }

  // Por defecto solo las campañas del usuario autenticado. `allUsers` trae todas
  // las que cumplen el filtro, para quien necesita elegir una que no es suya
  // (p. ej. transferir un chat a otra campaña).
  const getAllCampaigns = (
    filter?: CampaignOptionsFilter,
    options?: { allUsers?: boolean },
  ): Promise<IOptionsCampaign[]> => {
    const params: CampaignOptionsFilter = {
      ...filter,
      user_id: options?.allUsers ? undefined : (filter?.user_id ?? authStore.userId ?? undefined),
    }
    const requestKey = JSON.stringify([
      params.user_id ?? null,
      params.campaign_type ?? null,
      params.chat_channel ?? null,
    ])
    const pendingRequest = campaignOptionsRequests.get(requestKey)

    if (pendingRequest) return pendingRequest

    const request = privateApi
      .get<IOptionsCampaign[]>('/campaigns/options', { params })
      .catch((error: unknown) => {
        handleError(error, 'campaign.error_fetching_all_campaigns')
        throw error
      })
      .finally(() => {
        campaignOptionsRequests.delete(requestKey)
      })

    campaignOptionsRequests.set(requestKey, request)
    return request
  }

  const getCampaignById = (
    campaign_id: string,
    options?: { force?: boolean },
  ): Promise<IUpdateCampaign> => {
    const requestKey = `${authStore.userId ?? 'anonymous'}:${campaign_id}`
    const pendingRequest = campaignDetailRequests.get(requestKey)
    if (pendingRequest) return pendingRequest

    const cachedCampaign = campaignDetailCache.get(requestKey)
    if (cachedCampaign && !options?.force) return Promise.resolve(cachedCampaign)

    const request = privateApi
      .get<IUpdateCampaign>(`/campaigns/${campaign_id}`)
      .then((campaign) => {
        campaignDetailCache.set(requestKey, campaign)
        return campaign
      })
      .catch((error: unknown) => {
        handleError(error, 'campaign.error_fetching_campaign')
        throw error
      })
      .finally(() => {
        campaignDetailRequests.delete(requestKey)
      })

    campaignDetailRequests.set(requestKey, request)
    return request
  }

  const getCampaignByQueueId = async (queue_id: string): Promise<ICampaignByQueueResponse> => {
    try {
      return await privateApi.get<ICampaignByQueueResponse>(`/campaigns/campaign-queue/${queue_id}`)
    } catch (error) {
      handleError(error, 'campaign.error_fetching_campaign')
      throw error
    }
  }

  const getCampaignUsers = async (campaign_id: string) => {
    try {
      return await privateApi.get<
        {
          id: string
          name: string
          email?: string
          avatar?: string
          alias?: string | null
          connected: boolean
        }[]
      >(
        `/campaigns/${campaign_id}/users`,
      )
    } catch (error) {
      handleError(error, 'campaign.error_fetching_campaign_users')
      throw error
    }
  }

  const getShiftActivity = async (user_id: string, alias: string): Promise<IShiftActivity[]> => {
    try {
      return await privateApi.get<IShiftActivity[]>(`/campaigns/${user_id}/${alias}/shiftActivity`)
    } catch (error) {
      handleError(error, 'campaign.error_fetching_shift_activity')
      throw error
    }
  }

  const listCampaignKnowledgeDocuments = (
    campaign_id: string,
  ): Promise<ICampaignKnowledgeDocumentList> => {
    const requestKey = `${authStore.userId ?? 'anonymous'}:${campaign_id}`
    const pendingRequest = campaignKnowledgeDocumentListRequests.get(requestKey)
    if (pendingRequest) return pendingRequest

    const request = privateApi
      .get<ICampaignKnowledgeDocumentList>(`/campaigns/${campaign_id}/knowledge/documents`)
      .finally(() => {
        campaignKnowledgeDocumentListRequests.delete(requestKey)
      })

    campaignKnowledgeDocumentListRequests.set(requestKey, request)
    return request
  }

  const getCampaignKnowledgeDocument = async (
    campaign_id: string,
    document_id: string,
  ): Promise<ICampaignKnowledgeDocumentDetail> => {
    return await privateApi.get<ICampaignKnowledgeDocumentDetail>(
      `/campaigns/${campaign_id}/knowledge/documents/${document_id}`,
    )
  }

  const askCampaignKnowledgeQuestion = async (
    campaign_id: string,
    question: string,
  ): Promise<ICampaignKnowledgeChatResponse> => {
    return await privateApi.post<ICampaignKnowledgeChatResponse, { question: string }>(
      `/campaigns/${campaign_id}/knowledge/chat`,
      { question },
    )
  }

  return {
    clearCampaignCache,
    getCampaigns,
    getAllCampaigns,
    getCampaignById,
    getCampaignByQueueId,
    getCampaignUsers,
    getShiftActivity,
    listCampaignKnowledgeDocuments,
    getCampaignKnowledgeDocument,
    askCampaignKnowledgeQuestion,
  }
}
